import { FunctionComponent, useEffect, useRef, useState } from "react";
import { Breadcrumbs, styled } from "@horizontal-org/shira-ui";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LearnerBulkImportHeader } from "../LearnerBulkImportHeader";
import { ExitLearnerBulkImportModal } from "../modals/ExitLearnerBulkImportModal";
import { StartOverLearnerBulkImportModal } from "../modals/StartOverLearnerBulkImportModal";
import { FormattingGuidelinesModal } from "../modals/FormattingGuidelinesModal";
import { UploadCsvStep } from "./components/UploadCsvStep";
import { VerifyLearnersStep } from "./components/VerifyLearnersStep";
import { FinalReviewStep } from "./components/FinalReviewStep";
import { BulkInviteLearnersResponse, BulkInviteValidatedLearner, inviteLearnersBulk, verifyLearnersBulk } from "../../fetch/learner";
import { handleHttpError } from "../../fetch/handleError";
import { MobileResponsivenessBanner } from "../MobileResponsivenessBanner";

interface Props { }

export const LearnerBulkImportLayout: FunctionComponent<Props> = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const MAX_CSV_FILE_SIZE_BYTES = 10 * 1024 * 1024;

  const [step, handleStep] = useState(0);

  const [isExitBulkImportModalOpen, setIsExitBulkImportModalOpen] = useState(false);
  const [isStartOverBulkImportModalOpen, setIsStartOverBulkImportModalOpen] = useState(false);
  const [isFormattingGuidelinesOpen, setIsFormattingGuidelinesOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bulkInviteResponse, setBulkInviteResponse] = useState<BulkInviteLearnersResponse | null>(null);

  const [uploadError, setUploadError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const lastVerifiedFileKey = useRef<string | null>(null);
  const lastInvitedFileKey = useRef<string | null>(null);
  const lastInvitedCount = useRef<number | null>(null);

  const getInviteCount = (response: BulkInviteLearnersResponse | null) =>
    response?.filter((row) => row.status === "OK").length ?? 0;

  const getValidLearners = (response: BulkInviteLearnersResponse | null): BulkInviteValidatedLearner[] =>
    (response ?? [])
      .filter((row) => row.status === "OK" && row.name)
      .map((row) => ({
        row: row.row,
        email: row.email,
        name: row.name!,
      }));

  const validInviteCount = getInviteCount(bulkInviteResponse);

  const validateStep = () => {
    if (step === 0) {
      return !!selectedFile && !isFileLoading && !uploadError;
    }

    if (step === 1) {
      return !isFileLoading && validInviteCount > 0;
    }

    if (step === 2) {
      return !isSubmitting && validInviteCount > 0;
    }

    return true;
  };

  const handleFileChange = (file: File | null) => {
    if (!file) return;
    setSelectedFile(file);
    setBulkInviteResponse(null);
    setUploadError(null);

    lastVerifiedFileKey.current = null;
    lastInvitedFileKey.current = null;

    if (file.size > MAX_CSV_FILE_SIZE_BYTES) {
      setUploadError("file_too_large");
      return;
    }
  };

  const clearSelectedFile = () => {
    setIsFileLoading(false);
    setIsSubmitting(false);
    setSelectedFile(null);
    setBulkInviteResponse(null);
    // setIsDragging(false);
    setUploadError(null);

    lastVerifiedFileKey.current = null;
    lastInvitedFileKey.current = null;
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleStartOver = () => {
    clearSelectedFile();
    handleStep(0);
    setBulkInviteResponse(null);
  };

  useEffect(() => {
    if (step !== 1 || !selectedFile) {
      return;
    }

    const fileKey = `${selectedFile.name}-${selectedFile.size}-${selectedFile.lastModified}`;
    if (lastVerifiedFileKey.current === fileKey) {
      return;
    }

    setIsFileLoading(true);
    verifyLearnersBulk(selectedFile)
      .then((response) => {
        setBulkInviteResponse(response);
        lastVerifiedFileKey.current = fileKey;
      })
      .catch((error) => {
        const { message } = handleHttpError(error);
        setUploadError(message);
        handleStep(0);
        setBulkInviteResponse(null);
      })
      .finally(() => {
        setIsFileLoading(false);
      });
  }, [selectedFile, step]);

  const handleNext = () => {
    if (step === 2) {
      if (isSubmitting || !selectedFile) {
        return;
      }

      const fileKey = `${selectedFile.name}-${selectedFile.size}-${selectedFile.lastModified}`;
      if (lastInvitedFileKey.current === fileKey) {
        navigate("/learner", {
          state: {
            bulkInviteSent: { count: lastInvitedCount.current ?? getInviteCount(bulkInviteResponse) },
          },
        });
        return;
      }

      setIsSubmitting(true);
      const validLearners = getValidLearners(bulkInviteResponse);
      if (validLearners.length === 0) {
        setIsSubmitting(false);
        return;
      }

      inviteLearnersBulk(validLearners)
        .then((response) => {
          lastInvitedCount.current = getInviteCount(response);
          lastInvitedFileKey.current = fileKey;
          navigate("/learner", {
            state: {
              bulkInviteSent: { count: lastInvitedCount.current },
            },
          });
        })
        .catch((error) => {
          console.error("Failed to invite learners in bulk:", error);
        })
        .finally(() => {
          setIsSubmitting(false);
        });
      return;
    }

    handleStep(step + 1);
  };

  return (
    <>
      <ExitLearnerBulkImportModal
        isModalOpen={isExitBulkImportModalOpen}
        setIsModalOpen={setIsExitBulkImportModalOpen}
        onConfirm={() => {
          navigate(-1);
        }}
      />
      <StartOverLearnerBulkImportModal
        isModalOpen={isStartOverBulkImportModalOpen}
        setIsModalOpen={setIsStartOverBulkImportModalOpen}
        onConfirm={handleStartOver}
      />
      <FormattingGuidelinesModal
        isModalOpen={isFormattingGuidelinesOpen}
        setIsModalOpen={setIsFormattingGuidelinesOpen}
      />

      <MobileResponsivenessBanner />

      <LearnerBulkImportHeader
        onNext={handleNext}
        onBack={() => {
          if (step === 0) {
            setIsExitBulkImportModalOpen(true);
            return;
          }
          setIsStartOverBulkImportModalOpen(true);
        }}
        step={step}
        disableNext={!validateStep()}
        onExit={() => {
          setIsExitBulkImportModalOpen(true);
        }}
      />

      <Container>
        <ContentWrapper>
          <div>
            <ContentHeader id="learner-bulk-import-header">
              <Breadcrumbs
                active={step}
                items={[
                  { text: t('learners_bulk_import.tabs.upload_csv.tab_title') },
                  { text: t('learners_bulk_import.tabs.verify_learners.tab_title') },
                  { text: t('learners_bulk_import.tabs.review.tab_title') }
                ]}
              />
            </ContentHeader>

            {step === 0 && (
              <UploadCsvStep
                selectedFile={selectedFile}
                isFileLoading={isFileLoading}
                fileInputRef={fileInputRef}
                onFileChange={handleFileChange}
                onClearFile={clearSelectedFile}
                onOpenGuidelines={() => setIsFormattingGuidelinesOpen(true)}
                uploadError={uploadError}
              />
            )}

            {step === 1 && (
              <VerifyLearnersStep response={bulkInviteResponse} />
            )}

            {step === 2 && (
              <FinalReviewStep response={bulkInviteResponse} />
            )}
          </div>
        </ContentWrapper>
      </Container>
    </>
  );
};

const Container = styled.div`
  padding: 48px 0;
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ContentHeader = styled.div`
  padding-bottom: 12px;
`;
